import React, { useContext, useEffect, useState } from 'react'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Link,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Typography,
} from '@mui/material'
import { CancelOutlined, Close, ErrorOutline, PictureAsPdf, TerminalOutlined } from '@mui/icons-material'
import { api } from '../../../../services/Main/Api'
import Masks from '../../../../components/Shared/Masks'
import moment from 'moment/moment'
import Context from '../../../../contexts/Context'

const STATUS_COLOR = {
    0: 'info',
    2: 'warning',
    3: 'warning',
    5: 'success',
    7: 'error',
    8: 'error',
    9: 'error',
    10: 'error',
}

const PaymentDetailDialog = ({ open, paymentId, initialData, onClose, onCancel }) => {
    const { user } = useContext(Context)
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (!open) return
        if (initialData) {
            setData(initialData)
            return
        }
        if (!paymentId) return
        setLoading(true)
        setData(null)
        api.get(`/payment/${paymentId}/`)
            .then(res => setData(res.data))
            .catch(err => console.error(err))
            .finally(() => setLoading(false))
    }, [open, paymentId, initialData])

    const displayValue = data?.updated_value && Number(data.updated_value) > Number(data.principal_value)
        ? data.updated_value
        : data?.principal_value

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            scroll="paper"
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" color="primary.main">
                    Detalhes do Pagamento
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {loading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </Box>
                ) : data ? (
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                                #{data.number}
                            </Typography>
                            <Chip
                                color={STATUS_COLOR[data?.status?.value] || 'default'}
                                size="small"
                                variant="outlined"
                                label={data?.status?.label}
                            />
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        {data.has_error && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    p: 1.5,
                                    mb: 2,
                                    borderRadius: 1,
                                    bgcolor: 'error.light',
                                    color: 'error.contrastText',
                                }}
                            >
                                <ErrorOutline fontSize="small" />
                                <Typography variant="body2">
                                    {data.error_message || 'Ocorreu um problema ao tentar efetuar o pagamento.'}
                                </Typography>
                            </Box>
                        )}

                        <Table size="small">
                            <TableBody>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Beneficiário</TableCell>
                                    <TableCell>{data.bank_concessionare_favored}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Nº Documento</TableCell>
                                    <TableCell>{data.document_number ?? '-'}</TableCell>
                                </TableRow>
                                {data.linha_digitavel && (
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Linha Digitável</TableCell>
                                        <TableCell sx={{ wordBreak: 'break-all', fontSize: '0.75rem' }}>
                                            {data.linha_digitavel}
                                        </TableCell>
                                    </TableRow>
                                )}
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Vencimento</TableCell>
                                    <TableCell>
                                        {data.due_date
                                            ? moment(data.due_date).format('DD/MM/YYYY')
                                            : '-'}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Data de Pagamento</TableCell>
                                    <TableCell>
                                        {data.payment_date
                                            ? moment(data.payment_date).format('DD/MM/YYYY')
                                            : '-'}
                                    </TableCell>
                                </TableRow>
                                {data.user?.full_name && (
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Usuário</TableCell>
                                        <TableCell>{data.user.full_name}</TableCell>
                                    </TableRow>
                                )}
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Valor</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                                        {Masks.money(String(displayValue ?? 0))}
                                    </TableCell>
                                </TableRow>
                                {data.receipt && (
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Recibo</TableCell>
                                        <TableCell>
                                            <Link
                                                href={data.receipt}
                                                target="_blank"
                                                rel="noopener"
                                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                            >
                                                <PictureAsPdf fontSize="small" />
                                                Ver Recibo
                                            </Link>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>

                        {onCancel && data?.status?.value === 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    startIcon={<CancelOutlined />}
                                    onClick={() => onCancel(data)}
                                >
                                    Cancelar Pagamento
                                </Button>
                            </Box>
                        )}
                    </Box>
                ) : null}
            </DialogContent>

            <DialogActions sx={{ justifyContent: 'space-between' }}>
                {user?.is_superuser && paymentId ? (
                    <Button
                        size="small"
                        color="inherit"
                        startIcon={<TerminalOutlined />}
                        href={`/admin/logs?payment_id=${paymentId}`}
                        target="_blank"
                    >
                        Logs
                    </Button>
                ) : <Box />}
                <Button onClick={onClose}>Fechar</Button>
            </DialogActions>
        </Dialog>
    )
}

export default PaymentDetailDialog
